import React from 'react';
import { motion } from 'framer-motion';
import { 
  Facebook, 
  Linkedin, 
  Instagram, 
  Phone, 
  Mail, 
  MapPin, 
  ArrowUp, 
  ExternalLink 
} from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0a1118] text-white pt-12 md:pt-16 pb-8 px-6 relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl -mr-32 -mt-32"></div>

      <div className="max-w-7xl mx-auto">
        {/* Top Section: Branding and Newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12 border-b border-slate-800 pb-12">
          <div className="lg:col-span-1 flex flex-col items-center lg:items-start text-center lg:text-left">
            <h2 className="text-2xl font-black italic tracking-tighter mb-4">
              MARVEL <span className="text-orange-500 not-italic">SATO</span>
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm">
              Providing cutting-edge engineering solutions and industrial machinery for over two decades. Excellence in every lift, precision in every build.
            </p>
            <div className="flex gap-4">
              {[Facebook, Linkedin, Instagram].map((Icon, i) => (
                <motion.a
                  key={i}
                  href="#"
                  whileHover={{ y: -3, color: '#f97316' }}
                  className="bg-slate-800 p-2.5 rounded-lg text-slate-300 transition-colors"
                >
                  <Icon size={20} />
                </motion.a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 lg:gap-8">
            {/* Quick Links */}
            <div className="text-center sm:text-left">
              <h4 className="font-bold text-orange-500 uppercase tracking-wider text-xs mb-6">Navigation</h4>
              <ul className="space-y-4 text-sm text-slate-300">
                {['Home', 'About', 'Products', 'Gallery', 'Blog'].map((link) => (
                  <li key={link}>
                    <a href={`${link == "Home" ? "/" : link.toLowerCase()}`} className="hover:text-white flex items-center justify-center sm:justify-start group">
                      <span className="hidden sm:block w-0 group-hover:w-2 h-[2px] bg-orange-500 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industrial Categories */}
            <div className="text-center sm:text-left">
              <h4 className="font-bold text-orange-500 uppercase tracking-wider text-xs mb-6">Our Services</h4>
              <ul className="space-y-4 text-sm text-slate-300">
                {['Truck Cranes', 'Hydraulic Lifts', 'Maintenance'].map((item) => (
                  <li key={item}>
                    <a href="/" className="hover:text-white flex items-center justify-center sm:justify-start gap-1 group">
                      {item}
                      <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Details */}
            <div className="text-center sm:text-left sm:col-span-2 md:col-span-1">
              <h4 className="font-bold text-orange-500 uppercase tracking-wider text-xs mb-6">Contact Us</h4>
              <ul className="space-y-4 text-sm text-slate-300">
                <li className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
                  <MapPin size={18} className="text-orange-500 shrink-0" />
                  <span>Bellari Road, Hosepate<br className="hidden sm:block" />Karnataka, India - 680001</span>
                </li>
                <li className="flex flex-col sm:flex-row items-center gap-3">
                  <Phone size={18} className="text-orange-500 shrink-0" />
                  <span>+91 94498 26754 | +91 77600 73505</span>
                </li>
                
                <li className="flex flex-col sm:flex-row items-center gap-3">
                  <Mail size={18} className="text-orange-500 shrink-0" />
                  <span>marvelhpt02@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 pt-8 border-t border-slate-800/50">
          <div className="order-3 md:order-1 text-center md:text-left">
            <p className="text-slate-500 text-[10px] sm:text-xs">
              © {new Date().getFullYear()} Marvel SATO Pvt Ltd. Designed for Excellence.
            </p>
          </div>
          
          {/* Scroll to top button - Centered on mobile */}
          <div className="order-1 md:order-2">
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="bg-orange-500 p-3 rounded-full text-white shadow-lg shadow-orange-500/20"
              aria-label="Scroll to top"
            >
              <ArrowUp size={20} />
            </motion.button>
          </div>

          <div className="order-2 md:order-3 flex gap-6 text-[10px] sm:text-xs text-slate-500">
            <a href="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;